// services/OfflineDownloadService.js
import apiClient from 'api/client';
import offlineDBService from './dexieDB';
import { EventEmitter } from '../utils/eventEmitter';

// Create download events emitter for better communication between components
export const downloadEvents = new EventEmitter();

// Event names constants for better maintainability
export const DOWNLOAD_EVENTS = {
  STARTED: 'download:started',
  PROGRESS: 'download:progress',
  COMPLETED: 'download:completed',
  FAILED: 'download:failed',
  STEP_COMPLETED: 'download:step:completed'
};

const offlineDownloadService = {
  // Main download function that triggers all necessary downloads
  async downloadAllData(tenantId) {
    try {
      console.log(
        `Starting download for all offline data (tenant: ${tenantId})`
      );

      // Emit started event
      downloadEvents.emit(DOWNLOAD_EVENTS.STARTED);

      // Clear existing data first (excluding map tiles)
      await offlineDBService.clearAllExceptMapTiles();

      // Report progress
      downloadEvents.emit(DOWNLOAD_EVENTS.PROGRESS, {
        step: 'clear-data',
        completed: true,
        message: 'Cleared existing data'
      });

      // First download dropdown data (new format)
      downloadEvents.emit(DOWNLOAD_EVENTS.PROGRESS, {
        step: 'dropdowns',
        completed: false,
        message: 'Downloading dropdown options...'
      });

      const dropdownsDownloaded = await this.downloadDropdownOptions(tenantId);
      // console.log('Dropdowns downloaded:', dropdownsDownloaded);

      downloadEvents.emit(DOWNLOAD_EVENTS.STEP_COMPLETED, {
        step: 'dropdowns',
        success: dropdownsDownloaded
      });

      // Then download farmer form definitions
      downloadEvents.emit(DOWNLOAD_EVENTS.PROGRESS, {
        step: 'farmer-form',
        completed: false,
        message: 'Downloading farmer form definition...'
      });

      const formDownloaded = await this.downloadFarmerForm(tenantId);
      // console.log('Farmer form downloaded:', formDownloaded);

      downloadEvents.emit(DOWNLOAD_EVENTS.STEP_COMPLETED, {
        step: 'farmer-form',
        success: formDownloaded
      });

      // Finally download farm form definitions (which needs dropdown data)
      downloadEvents.emit(DOWNLOAD_EVENTS.PROGRESS, {
        step: 'farm-form',
        completed: false,
        message: 'Downloading farm form definition...'
      });

      const farmDownloaded = await this.downloadFarmForm(tenantId);
      // console.log('Farm form downloaded:', farmDownloaded);

      downloadEvents.emit(DOWNLOAD_EVENTS.STEP_COMPLETED, {
        step: 'farm-form',
        success: farmDownloaded
      });

      downloadEvents.emit(DOWNLOAD_EVENTS.PROGRESS, {
        step: 'cultivation-form',
        completed: false,
        message: 'Downloading cultivation form definition...'
      });

      const cultivationFormDownloaded = await this.downloadCultivationForm(
        tenantId
      );
      // console.log('Cultivation form downloaded:', cultivationFormDownloaded);

      downloadEvents.emit(DOWNLOAD_EVENTS.STEP_COMPLETED, {
        step: 'cultivation-form',
        success: cultivationFormDownloaded
      });

      // Check database status
      const dbStatus = await offlineDBService.checkDatabaseStatus();
      // console.log('Database status after download:', dbStatus);

      const result = {
        success:
          formDownloaded &&
          farmDownloaded &&
          cultivationFormDownloaded &&
          dropdownsDownloaded,
        formDownloaded,
        farmDownloaded,
        dropdownsDownloaded,
        dbStatus
      };

      // Emit completed event with final result
      downloadEvents.emit(
        result.success ? DOWNLOAD_EVENTS.COMPLETED : DOWNLOAD_EVENTS.FAILED,
        result
      );

      return result;
    } catch (error) {
      console.error('Error downloading all data:', error);

      const result = {
        success: false,
        error: error.message
      };

      // Emit failed event with error
      downloadEvents.emit(DOWNLOAD_EVENTS.FAILED, result);

      return result;
    }
  },

  // Download form data and store in Dexie
  async downloadFarmerForm(tenantId) {
    try {
      const response = await apiClient.get(`digitization/v1/farmer/form`);
      // console.log('Form response:', response.data);

      if (response.data && response.data.success) {
        // Store the form definition in Dexie
        await offlineDBService.saveFormDefinition(
          'farmer-create',
          response.data.results
        );
        // console.log('Farmer form downloaded and stored successfully');
        return true;
      } else {
        console.error('Failed to download farmer form:', response.data);
        return false;
      }
    } catch (error) {
      console.error('Error downloading farmer form:', error);
      return false;
    }
  },

  async downloadFarmForm(tenantId) {
    try {
      const response = await apiClient.get(
        `digitization/v1/farm/form?is_edit=false&offline=true`
      );
      console.log('Form response:', response.data);

      if (response.data && response.data.success) {
        // Add a new field to the response data
        if (response.data.results && response.data.results.fields) {
          let options = [];
          const dropdownData = await offlineDBService.getDropdownData();

          if (dropdownData?.results?.farmer) {
            options = dropdownData.results.farmer;
            // console.log('Got farmer dropdown options:', options.length);
          } else {
            console.warn('Farmer dropdown options not found');
          }

          // Create the new field to add
          const newField = {
            id: 'farmer',
            type: 'dropdown',
            label: 'Farmer',
            options: options || [],
            isEditable: true,
            validation: {
              required: true
            },
            placeholder: 'Select Farmer',
            defaultValue: ''
          };

          // Insert the new field as the second item in the array (index 1)
          if (response.data.results.fields.length > 0) {
            response.data.results.fields.splice(1, 0, newField);
          } else {
            // If the array is empty, just add it
            response.data.results.fields.push(newField);
          }
        }

        // Store the modified form definition in Dexie
        await offlineDBService.saveFormDefinition(
          'farm-create',
          response.data.results
        );
        // console.log('Farm form downloaded and stored successfully');
        return true;
      } else {
        console.error('Failed to download farm form:', response.data);
        return false;
      }
    } catch (error) {
      console.error('Error downloading farm form:', error);
      return false;
    }
  },

  // NEW METHOD: Download cultivation form definition
  async downloadCultivationForm(tenantId) {
    try {
      const response = await apiClient.get(
        `digitization/v1/cultivation/form?is_edit=false&is_view=false&is_offline=true
`
      );
      // console.log('Cultivation form response:', response.data);

      if (response.data && response.data.success) {
        // Add farm dropdown field to the form if needed
        if (response.data.results && response.data.results.fields) {
          let farmOptions = [];
          const dropdownData = await offlineDBService.getDropdownData();

          // Add farm field if you need to select a farm for cultivation
          if (dropdownData?.results?.farm) {
            farmOptions = dropdownData.results.farm;
            // console.log('Got farm dropdown options:', farmOptions.length);

            // Create the farm field to add
            const farmField = {
              id: 'farm',
              type: 'dropdown',
              label: 'Farm',
              options: farmOptions || [],
              isEditable: true,
              validation: {
                required: true
              },
              placeholder: 'Select Farm',
              defaultValue: ''
            };

            // Insert the farm field (check where it needs to be placed based on your form structure)
            // This is just an example - you may need to adjust based on your actual form structure
            if (response.data.results.fields.length > 0) {
              // Insert at appropriate position - adjust index as needed
              response.data.results.fields.splice(1, 0, farmField);
            } else {
              response.data.results.fields.push(farmField);
            }
          }
        }

        // Store the cultivation form definition in Dexie
        await offlineDBService.saveFormDefinition(
          'cultivation-create', // Use consistent key naming
          response.data.results
        );
        // console.log('Cultivation form downloaded and stored successfully');
        return true;
      } else {
        console.error('Failed to download cultivation form:', response.data);
        return false;
      }
    } catch (error) {
      console.error('Error downloading cultivation form:', error);
      return false;
    }
  },
  // Download all dropdown options for offline use
  async downloadDropdownOptions(tenantId) {
    try {
      // console.log('Downloading all dropdown options for offline use');

      // Call API to get all dropdown options
      const response = await apiClient.get(`digitization/v1/offline/tables`);

      if (response.data && response.data.success) {
        // Save the entire dropdown options structure to IndexedDB
        await offlineDBService.saveDropdownData(
          'dropdownOptions',
          response.data
        );
        // console.log('Successfully downloaded and saved all dropdown options');
        return true;
      }

      console.error('API did not return success status for dropdown options');
      return false;
    } catch (error) {
      console.error('Failed to download dropdown options:', error);
      return false;
    }
  },

  // Backward compatibility for table data method
  async downloadTableData(tenantId) {
    console.warn(
      'downloadTableData is deprecated. Use downloadDropdownOptions instead.'
    );
    return this.downloadDropdownOptions(tenantId);
  },

  // Log all downloaded data to console (useful for debugging)
  async logAllDownloadedData() {
    try {
      console.log('=== DOWNLOADED DATA SUMMARY ===');

      // Log form definitions
      const farmerForm = await offlineDBService.getFormDefinition(
        'farmer-create'
      );
      // console.log('Farmer Form available:', !!farmerForm);

      const farmForm = await offlineDBService.getFormDefinition('farm-create');
      // console.log('Farm Form available:', !!farmForm);

      const cultivationForm = await offlineDBService.getFormDefinition(
        'cultivation-create'
      );
      // console.log('Cultivation Form available:', !!cultivationForm);

      // Log dropdown data
      const dropdownData = await offlineDBService.getDropdownData();
      // console.log('Dropdown Data available:', !!dropdownData);

      if (dropdownData && dropdownData.results) {
        // console.log(
        //   'Dropdown fields available:',
        //   Object.keys(dropdownData.results)
        // );

        // Log a sample of each field type
        Object.keys(dropdownData.results).forEach(fieldType => {
          const fieldData = dropdownData.results[fieldType];
          if (fieldData) {
            if (typeof fieldData === 'object' && !Array.isArray(fieldData)) {
              // Nested structure
              const firstKey = Object.keys(fieldData)[0];
              if (firstKey) {
                console.log(`Sample of ${fieldType}:`, {
                  key: firstKey,
                  sample: fieldData[firstKey].slice(0, 1)
                });
              }
            } else if (Array.isArray(fieldData)) {
              // Direct array
              console.log(`Sample of ${fieldType}:`, fieldData.slice(0, 1));
            }
          }
        });
      }

      console.log('=== END DATA SUMMARY ===');

      return true;
    } catch (error) {
      console.error('Error logging downloaded data:', error);
      return false;
    }
  }
};

export default offlineDownloadService;
